export * from "./defaults";
export * from "./initApi";

import { WeekDayNames } from "./entities/hours";

export { Client } from "./client";
export { ActionsClient } from "./endpoints/actions";
export type {
  ApiToken,
  ApiTokenListResponse,
  CreateApiTokenPayload,
  UpdateApiTokenPayload
} from "./endpoints/api-tokens";
export * from "./endpoints/avigilon";
export type { BillingPortalResponse } from "./endpoints/billing";
export { Billing } from "./endpoints/billing";
export {
  type BulkImport,
  type BulkImportCounts,
  type BulkImportDateFormat,
  type BulkImportItem,
  type BulkImportItemReferenceType,
  type BulkImportItemStatus,
  type BulkImportStatus,
  BulkImportsClient,
  type BulkImportType,
  type CreateUploadResponse,
  type ListItemsParams
} from "./endpoints/bulk-imports";
export * from "./endpoints/calendar-auth";
export * from "./endpoints/core-integrations";
export * from "./endpoints/demo-data";
export type {
  Division,
  DivisionAddPayload,
  DivisionsFilter,
  DivisionsTree,
  DivisionType,
  DivisionUpdatePayload
} from "./endpoints/divisions";
export { Enterprises } from "./endpoints/enterprises";
export { type External, Externals } from "./endpoints/externals";
export { Forms } from "./endpoints/forms";
export { Integrations } from "./endpoints/integrations";
export { Invitations } from "./endpoints/invitations";
export {
  type McpAuthorizationAnswer,
  type McpAuthorizationRequest,
  McpClient,
  type McpConnection,
  type McpConnectionListResponse
} from "./endpoints/mcp";
export { Organizations } from "./endpoints/organizations";
export {
  type PageFilter,
  type PageSectionAddPayload,
  type PageSectionFilter,
  type PageSectionUpdatePayload,
  Pages
} from "./endpoints/pages";
export { PermissionGroupsClient } from "./endpoints/permission-groups";
export { Reports } from "./endpoints/reports";
export { ApiClientError } from "./endpoints/response/ApiClientError";
export type { Id } from "./endpoints/response/id";
export { ResponseWithStatus } from "./endpoints/response/response_with_status";
export { type ScheduleParamsAll, Schedules } from "./endpoints/schedules";
export { GlobalSearch } from "./endpoints/search";
export { Signups } from "./endpoints/signups";
export type {
  StationsFilter,
  StationVisitsFilter
} from "./endpoints/stations";
export { Uploads } from "./endpoints/uploads";
export { Users } from "./endpoints/users";
export { WorkplaceRequestCategoriesClient } from "./endpoints/workplace-request-categories";
export { WorkplaceRequestCommentsClient } from "./endpoints/workplace-request-comments";
export { WorkplaceRequestDashboardPublicClient } from "./endpoints/workplace-request-dashboard-public";
export { WorkplaceRequestDashboardsClient } from "./endpoints/workplace-request-dashboards";
export { WorkplaceRequestEventsClient } from "./endpoints/workplace-request-events";
export { WorkplaceRequestSharingClient } from "./endpoints/workplace-request-sharing";
export { WorkplaceRequestTypesClient } from "./endpoints/workplace-request-types";
export {
  type FloorHealth,
  type Placement,
  type Space,
  type SpaceKind,
  type Zone,
  type ZoneCreatePayload,
  type ZoneDetail,
  type ZoneOfficeSummary,
  type ZoneSummary,
  ZonesClient,
  type ZonesHealth,
  type ZoneTreeNode,
  type ZoneUpdatePayload
} from "./endpoints/zones";
export type {
  AuthenticatedOrganization,
  AuthenticatedUser,
  Permissions
} from "./entities/authenticated_user";
export * from "./entities/calendar_auth";
export type {
  Connection,
  CreateConnectionRequest,
  UpdateConnectionRequest
} from "./entities/connection";
export type {
  DedicatedExceptionRelease,
  DedicatedExceptionRequest,
  DedicatedExceptionResponse,
  IDedicatedException
} from "./entities/dedicated_exception";
export type {
  CreateDirectoryRequest,
  Directory,
  UpdateDirectoryRequest
} from "./entities/directory";
export type { Form } from "./entities/form";
export type { FormRequest } from "./entities/form_request";
export type { GanttItem, GanttOverviewResponse } from "./entities/gantt";
export type {
  HealthGroup,
  HealthStatus,
  HealthTestResult,
  HealthTestType,
  IHealth,
  IHealthTransition
} from "./entities/health_status";
export {
  ALL_WEEK_DAYS,
  type HourQueryParams,
  type HourType,
  type IHour,
  type IHourPost
} from "./entities/hours";
export { IntegrationType } from "./entities/integrations/integration_type";
export type { InvitationRequest } from "./entities/invitation";
export {
  ALL_LEAVE_POLICY_TYPES,
  type IAttendanceRequestType,
  type LeavePolicy,
  type LeavePolicyParams,
  type LeavePolicyType,
  LeavePolicyTypeDisplay,
  type LeaveRequestsParams
} from "./entities/leave";
export type { INews } from "./entities/news";
export type {
  Office,
  OfficeAddPayload,
  OfficeUpdatePayload,
  OfficeWithFloors
} from "./entities/office";
export type { ReservationSettingsRequest } from "./entities/office_settings_request";
export * from "./entities/office-document";
export * from "./entities/offices_availability";
export type {
  Organization,
  OrganizationFeatures
} from "./entities/organization";
export type {
  GeneralSettingsRequest,
  QuestionnaireSettingsRequest
} from "./entities/organization_settings_request";
export type { Period } from "./entities/period";
export {
  type IPermissionGroup,
  type IPermissionGroupCreate,
  type IPermissionGroupUpdate,
  PERMISSION_GROUP_FEATURE_KEYS,
  PERMISSION_GROUP_FEATURE_LABELS,
  type PermissionGroupFeatureKey
} from "./entities/permission_group";
export {
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED,
  PRESENCE_TYPE_IN_PERSON,
  PRESENCE_TYPE_OOO,
  PRESENCE_TYPE_REMOTE,
  type PresenceStatus,
  type PresenceType
} from "./entities/presence";
export type { IQuestionItem, IQuestionWithAnswer } from "./entities/question";
export type {
  IQuestionnaire,
  QuestionnaireRequest
} from "./entities/questionnaire";
export type {
  IResource,
  MapCompositeType,
  MapIcon,
  Resource,
  ResourceMapVersion,
  ResourceType
} from "./entities/resource";
export type {
  AssignmentGroupType,
  IResourcePermission
} from "./entities/resource_permission";
export type {
  CreateResourceRequest,
  EditResourceRequest
} from "./entities/resource_requests";
export type {
  AvailabilityError,
  AvailabilityErrorCode,
  AvailabilityErrorDetails,
  AvailabilityErrorHourDetails,
  AvailabilityErrorOfficeFutureLimitDetails,
  AvailabilityErrorOfficeNotAvailableDetails,
  AvailabilityErrorOverCapacityDetails,
  AvailabilityErrorResourceUnavailableDetails,
  ISchedule,
  OnlineMeetingProvider,
  OnlineMeetingStatus,
  ScheduleOrigin
} from "./entities/schedule";
export type {
  AttendeeRequest,
  CreateOrUpdateScheduleFromIntegrationsRequest,
  FromIntegrationsScheduleRequest,
  ScheduleRequest,
  ScheduleRequestUpdate,
  ScheduleSlotRequest,
  ScheduleSlotRequestUpdate
} from "./entities/schedule_request";
export type { ScheduleType } from "./entities/schedule_type";
export type { ISlot } from "./entities/slot";
export {
  STATION_TYPE_ROOM,
  STATION_TYPE_VISITOR,
  type StationType
} from "./entities/station";
export type {
  ITeam,
  ITeamData,
  ITeamType,
  TeamChildren
} from "./entities/team";
export {
  type IDedicatedResourceRef,
  type IUserFit,
  type IUserFitSimple,
  type IUserFull,
  type IUserSelectable,
  type UserRoles,
  UserRolesLabels
} from "./entities/user";
export type { IUserResourceManaged } from "./entities/user_resource";
export type {
  TeamEditRequestBody,
  UserEditRequestBody
} from "./entities/user_team_request";
export type {
  IDocument,
  IDocumentPartial,
  IDocumentRejectable,
  ITestDocument,
  ITestDocumentForm,
  ITestDocumentPartial,
  IVaccinationDocument,
  IVaccinationDocumentForm,
  IVaccinationDocumentPartial,
  IVaccine
} from "./entities/vaccine";
export {
  type ScheduleVisibility,
  VISIBILITY_CONFIDENTIAL,
  VISIBILITY_PRIVATE,
  VISIBILITY_PUBLIC
} from "./entities/visibility";
export { GoogleCalendarTransformer } from "./integrations/google";
export { MicrosoftCalendarTransformer } from "./integrations/microsoft";
// TODO(workplace-requests): remove when the mock overlay is no longer needed.
export * from "./mocks/workplace-requests-mock";
export type { Settings } from "./settings";
export * from "./types/actions";
export * from "./types/agenda";
export * from "./types/api-types";
export {
  DATE_FORMAT_STYLE_DMY,
  DATE_FORMAT_STYLE_MDY,
  DATE_FORMAT_STYLE_YMD,
  type DateFormatStyle,
  type IOfficesSchedules,
  type IWorkingLocation,
  type IWorkingLocationPresenceType,
  type IWorkingLocations,
  type UserRequest,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE
} from "./types/api-types";
export * from "./types/core-integration-types/index";
export * from "./types/favorites";
export * from "./types/global-search-types";
export * from "./types/integration-types";
export * from "./types/my-calendar-types";
export * from "./types/reporting";
// Settings
export * from "./types/settings-types";
export * from "./types/table-overview";
export * from "./types/tags";
export * from "./types/workplace-request-categories";
export * from "./types/workplace-request-comments";
export * from "./types/workplace-request-dashboards";
export * from "./types/workplace-request-events";
export * from "./types/workplace-request-types";
export * from "./types/workplace-requests";
export { formatOptions } from "./utils";
export {
  cover24Hours,
  parseDateOnlyString,
  parseTimeOnly,
  prettifyDateOnlyString,
  prettifyDateOnlyStringTemplate,
  prettyTimeOnly
} from "./utils/datetime";
export * from "./utils/defaultReservationTimeSettings";
export { isParking } from "./utils/resource-utils";
export { getScheduleResources } from "./utils/schedule-utils";
export type { WeekDayNames };

export const V2_REDESIGN_FORCE_DATE = new Date("2025-07-01");

export * from "./endpoints/notifications";
// Visitor Manager
export {
  type CheckInVisitPayload,
  type CreateInvitationVisitPayload,
  DeliveriesClient,
  type DeliveriesFilter,
  type Delivery,
  type DeliveryCreatePayload,
  type DeliveryStatus,
  type DeliveryType,
  type DeliveryUpdatePayload,
  type DeliveryUploadType,
  type GenerateDeliveryUploadURLPayload,
  type GenerateImageUploadURLPayload,
  type GenerateImageUploadURLResponse,
  type GenerateRemoteCodeResponse,
  type ImageUploadFormat,
  type ImageUploadType,
  type InvitationCodeResponse,
  type LoginStationPayload,
  type LoginStationResponse,
  type LogoutStationPayload,
  type NotificationEmailType,
  type OfficeWithStations,
  PublicSharingVisitsClient,
  type PublicSharingVisitsFilter,
  type PublicVisit,
  type PublicVisitsSharingResponse,
  type ScanDeliveryLabelPayload,
  type ScanDeliveryLabelResponse,
  type SignedDocument,
  type SignedDocumentsFilter,
  type StationDeliveryCreatePayload,
  type StationImageUploadPayload,
  type StationResource,
  type TestNotificationPayload,
  type UpdateOfficeSharingPayload,
  type Visit,
  type VisitCreatePayload,
  type VisitFieldValue,
  type VisitHost,
  type Visitor,
  type VisitorDocument,
  type VisitorListItem,
  VisitorNotificationsClient,
  type VisitorSettings,
  type VisitorSettingsUpdatePayload,
  type VisitorSignature,
  type VisitorSignaturesFilter,
  type VisitorStation,
  type VisitorStationBase,
  type VisitorStationResponseBase,
  type VisitorStationType,
  VisitorsClient,
  type VisitorsFilter,
  type VisitorType,
  type VisitorTypeField,
  type VisitorTypeFieldType,
  type VisitorTypeHost,
  type VisitorTypeMinimal,
  type VisitorTypeStep,
  type VisitorTypeStepType,
  type VisitRegistration,
  type VisitSignature,
  type VisitStatus,
  type VisitsFilter,
  type VMNotificationConfig,
  type VMNotificationSettings,
  type WayfinderAssignment,
  type WayfinderAssignmentsResponse,
  type WayfinderPerson,
  type WayfinderRoom,
  type WayfinderSearchParams
} from "./endpoints/visitor-manager";
export type { NotificationSettings } from "./entities/user_settings";

export { shouldShowV2SwitchForAllUsers } from "./feature-flags";
export * from "./types/notifications";
